import { isMiniProgram } from '@/utils/wechat';

// Full coverage event（Lead campaign）
export const FULL_COVERAGE_PIXEL_ID = '906047510780866';
// Full funnel events _Goal Sales（Sales campaign，本期新增）
export const FULL_FUNNEL_GOAL_SALES_PIXEL_ID = '946488531438337';
// Purchase w/ Value（维持分渠道，由 FbPurchasePixelScript 单独 init）
export const PURCHASE_PIXEL_ID = '1032673631108666';

// 悟空 app webview 内打开（落地页通过 url query type=app_android / app_ios 标识）
// 与小程序一样：禁止加载 FB Pixel SDK，避免 app 端平台审核 / 隐私合规问题
export const isInWuKongApp = () => {
  try {
    if (typeof window === 'undefined') return false;
    const type = new URLSearchParams(window.location.search).get('type') || '';
    return type.includes('app_android') || type.includes('app_ios');
  } catch (e) {
    return false;
  }
};

export const FB_PIXEL_IDS = [FULL_COVERAGE_PIXEL_ID, FULL_FUNNEL_GOAL_SALES_PIXEL_ID] as const;

// 历史保留：默认对外暴露的单 Pixel ID（其他场景如 PageView 沿用原值）
export const FB_PIXEL_ID = '495409691452971';

// FB OAuth 进来的用户在落地页/约课页不再回传 FB Pixel，避免与 FB 自身归因双计
const FB_OAUTH_SKIP_KEY = 'FB_OAUTH_SKIP_PIXEL';

export type FbBusinessEvent = 'Lead' | 'Schedule';

const EVENT_NAME_MAP: Record<FbBusinessEvent, { fullCoverage: string; fullFunnel: string }> = {
  Lead: { fullCoverage: 'Lead', fullFunnel: 'Complete Registration' },
  Schedule: { fullCoverage: 'Schedule', fullFunnel: 'Start trial' },
};

export const markFbOAuthSkipPixel = () => {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(FB_OAUTH_SKIP_KEY, 'true');
  } catch (e) {
    /* noop */
  }
};

export const clearFbOAuthSkipPixel = () => {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(FB_OAUTH_SKIP_KEY);
  } catch (e) {
    /* noop */
  }
};

// 除 Pixel skip 判定外，也用作「本次会话由 FB OAuth 进入约课页」的场景识别信号
export const isFbOAuthSkipActive = () => {
  try {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(FB_OAUTH_SKIP_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

// 仅环境兜底：fbq 是否可调用 + 当前是否在小程序 / 悟空 app webview
// 1032/Purchase 不进入 trackFbBusinessEvent 封装、也不受 FB OAuth skip 影响
//（详见 docs/2026-04-29-fb-pixel-full-funnel-events.md 第 3.1 / 3.3-A 节），
// 但同样需要这层环境兜底，避免在小程序 webview 里 fbq 未注入时直接 TypeError。
export const shouldSkipFbPixelByEnv = () => {
  if (typeof window === 'undefined' || !window.fbq) return true;
  if (isMiniProgram() || isInWuKongApp()) return true;
  return false;
};

const shouldSkipFbPixel = () => {
  if (shouldSkipFbPixelByEnv()) return true;
  if (isFbOAuthSkipActive()) return true;
  return false;
};

const buildEventOptions = (eventID?: string) => (eventID ? { eventID } : undefined);

/**
 * 单一 pixel 回退分支需要的额外选项。当前只暴露一个开关 `overridePixelId`：
 *
 * - **未传 / 空字符串**：默认双发到 906（Full coverage event）+ 946（Full
 *   funnel events _Goal Sales），与历史行为完全一致。
 *
 * - **传入合法 pixel id**：跳过双发，**只**向该 pixel 发送一条事件，事件名
 *   使用 `EVENT_NAME_MAP[event].fullCoverage`（如 `Schedule` / `Lead`），
 *   不发 Sales-campaign 那条 `fullFunnel` 事件名（如 `Start trial` /
 *   `Complete Registration`）。
 *
 * 主要场景是 ai-landing-super（wukong-math AI 落地页）约课成功路径：上游
 * 在留资阶段已经向自有 919 pixel 打过 Lead，约课成功也需要落到同一个 919；
 * 不希望再被 cp-landing 默认的 906 / 946 双发覆盖（否则同一个 trackId 会
 * 同时出现在三个 pixel 的 Events Manager 里，归因报表对不齐）。
 *
 * 调用方决定是否走该分支（通常读 `pageConfig.fromAiLanding` 配合
 * `pageConfig.fbPixelId`）；本函数**只**负责按参数分流，不做来源判断。
 *
 * `shouldSkipFbPixel()`（小程序 / 悟空 app webview / FB OAuth skip）依然
 * 优先生效，覆盖任何上报。
 */
interface FbBusinessEventOptions {
  overridePixelId?: string;
}

// 业务事件统一上报：自动同时回传到 906（Full coverage event）+ 946（Full funnel events _Goal Sales）
// Purchase w/ Value（1032）不在本次范围，沿用各页面原有逻辑，不在此封装
export const trackFbBusinessEvent = (
  event: FbBusinessEvent,
  params: Record<string, any> = {},
  eventID?: string,
  options: FbBusinessEventOptions = {},
) => {
  if (shouldSkipFbPixel()) return;
  const names = EVENT_NAME_MAP[event];
  const fbqOptions = buildEventOptions(eventID);
  const overridePixelId = options.overridePixelId?.trim();
  if (overridePixelId) {
    // 单 pixel 分支：只发 fullCoverage 事件名（"Schedule" / "Lead"），
    // 不发 Sales-campaign 的 "Start trial" / "Complete Registration"。
    window.fbq('trackSingle', overridePixelId, names.fullCoverage, params, fbqOptions);
    return;
  }
  window.fbq('trackSingle', FULL_COVERAGE_PIXEL_ID, names.fullCoverage, params, fbqOptions);
  window.fbq('trackSingle', FULL_FUNNEL_GOAL_SALES_PIXEL_ID, names.fullFunnel, params, fbqOptions);
};

// 兼容旧调用（保留导出，但本次需求建议改用 trackFbBusinessEvent）
const pageView = (name: string) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', name);
};

const eventTrack = (name: string, options = {}) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('track', name, options);
};

const eventTrackSingle = (eventName: string) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  window.fbq('trackSingle', FB_PIXEL_ID, eventName);
};

export { pageView, eventTrack, eventTrackSingle };
